import { computed, inject, Injectable, signal } from '@angular/core';
import { CurrentUserService, IdentityActor } from '@/app/core/current-user/current-user.service';

/**
 * The set of tenants the current principal is acting on. Foundation states: `unbounded` (every tenant the actor
 * can reach — the operator: a global super-admin or any holder of a global flag) and `bounded` (an explicit set — a
 * tenant administrator's or grant holder's administered tenants). A future `group` variant slots in here; the
 * discriminated union forces every consumer to handle the new case rather than silently defaulting. See ADR:
 * scope-readiness foundation.
 */
export type Scope = { kind: 'unbounded' } | { kind: 'bounded'; tenantIds: string[] };

@Injectable({ providedIn: 'root' })
export class ScopeService {
    private readonly currentUser = inject(CurrentUserService);

    private readonly scope = signal<Scope>({ kind: 'unbounded' });

    /** The tenants currently in scope. Data-access services read this instead of assuming "all tenants". */
    readonly currentScope = this.scope.asReadonly();

    /** True when the principal acts across every tenant (the operator default today). */
    readonly isUnbounded = computed(() => this.scope().kind === 'unbounded');

    /** The actor the scope was last derived from (`undefined`: never derived). */
    private derivedFrom: IdentityActor | null | undefined = undefined;

    /**
     * Derives the initial scope from the signed-in actor (NOT a picker):
     * - the OPERATOR — a global super-admin or the holder of any global flag, the same test `PersonaService.isOperator`
     *   makes — is unbounded, as is an actor with no tenant. The server already narrows every list per area for a reader
     *   without that area's global flag, so an operator's list is never wider than what it may read; narrowing it to the
     *   sign-in tenant on top made the dashboard's global counts and the lists its tiles open disagree (a users-flag
     *   operator saw "3 zaključana naloga" and an empty `/korisnici?status=zakljucani`).
     * - everyone else (a tenant administrator, a grant holder) is bounded to their administered tenant set — home tenant
     *   ∪ the tenants of their live delegation grants (ADR 0002). For a grant-less single-tenant actor that set is just
     *   their own tenant; a reseller sees all the tenants they administer.
     *
     * Derived once per loaded actor: `currentUserGuard` runs on every navigation inside the shell (T1-07), and deriving
     * again for the same actor would hand every list that reads the scope a new value — a reload — on each click.
     */
    initializeFromActor(): void {
        const actor = this.currentUser.actor();
        if (actor === this.derivedFrom) return;
        this.derivedFrom = actor;
        // `holdsGlobalFlag` is read from CurrentUserService directly: PersonaService would make an injection cycle here.
        if (!actor || actor.isGlobalSuperAdmin || this.currentUser.holdsGlobalFlag() || !actor.tenantId) {
            this.scope.set({ kind: 'unbounded' });
            return;
        }
        const administered = actor.administeredTenantIds?.length ? actor.administeredTenantIds : [actor.tenantId];
        this.scope.set({ kind: 'bounded', tenantIds: administered });
    }

    /**
     * Set the active scope. Nothing writes it since T1-07: the topbar's tenant jump NAVIGATES to a tenant's hub instead of
     * narrowing the lists. A future group selection would write here.
     */
    setScope(next: Scope): void {
        this.scope.set(next);
    }

    reset(): void {
        this.derivedFrom = undefined;
        this.scope.set({ kind: 'unbounded' });
    }
}
